import { useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  isCurrentUserAdminOrGlobalAdminSelector,
  useGetUsersQuery
} from '@axilon-apps/domains-identity/state'
import {
  useAppSelector,
  useGetUsageSummaryQuery,
  type UsageUserSummary
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { cn } from '../../lib/utils'
import { formatAvgTurns, formatTimeSpent, usersWithKeyedCount } from './format'

const RANGE_PRESETS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' }
] as const

const FEATURE_LABELS: Record<string, string> = {
  graph: 'Graph',
  plant_view: 'Plant',
  agent_results: 'Runs',
  configure_agents: 'Configure',
  dashboards_view: 'Dashboards',
  dashboard_builder: 'Dashboard builder',
  chat_llm: 'Chat',
  reports: 'Reports',
  ingest: 'Ingest',
  tags: 'Tags',
  notifications: 'Notifications',
  settings: 'Settings',
  branches: 'Branches',
  other: 'Other'
}

function rangeForDays(days: number): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

function displayName(
  userId: string,
  usersById: Map<string, { name?: string; username?: string }>
): string {
  const user = usersById.get(userId)
  const label = user?.name?.trim() || user?.username?.trim()
  return label || userId
}

function featureLabel(feature: string): string {
  return FEATURE_LABELS[feature] ?? feature
}

export function UsageAnalyticsPage() {
  const canViewUsage = useAppSelector(isCurrentUserAdminOrGlobalAdminSelector)
  const [days, setDays] = useState<(typeof RANGE_PRESETS)[number]['days']>(7)
  const range = useMemo(() => rangeForDays(days), [days])
  const { data, isLoading, isFetching, error } = useGetUsageSummaryQuery(range, {
    skip: !canViewUsage
  })
  const { data: users } = useGetUsersQuery(
    { page: 1, perPage: 100, enabled: true },
    { skip: !canViewUsage }
  )

  const usersById = useMemo(() => {
    const map = new Map<string, { name?: string; username?: string }>()
    for (const user of users?.items ?? []) {
      if (user.id) map.set(user.id, user)
    }
    return map
  }, [users?.items])

  if (!canViewUsage) return <Navigate to="/plant" replace />

  const org = data?.org
  const maxDau = Math.max(1, ...(org?.dau.map((point) => point.users) ?? [0]))

  return (
    <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="m-0 text-xl font-semibold tracking-tight text-ink">Usage</h1>
          <p className="mt-1 mb-0 text-13 text-ink-3">
            Who used this organization, how long they stayed, and how much they chatted.
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {RANGE_PRESETS.map((preset) => (
            <Button
              key={preset.days}
              type="button"
              size="sm"
              variant={days === preset.days ? 'default' : 'outline'}
              onClick={() => setDays(preset.days)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </header>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading usage…</p> : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          Usage could not be loaded.
        </p>
      ) : null}

      {org ? (
        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-busy={isFetching}>
          <StatCard label="Active users" value={String(org.active_users)} />
          <StatCard
            label="Time spent"
            value={formatTimeSpent(org.time_spent_seconds)}
            hint="Focused tab time, in minutes"
          />
          <StatCard
            label="Conversations"
            value={String(org.chat.conversations_started)}
            hint={`${formatAvgTurns(org.chat.avg_turns_per_conversation)} turns avg`}
          />
          <StatCard label="Chat turns" value={String(org.chat.turns)} />
        </section>
      ) : null}

      {org && org.dau.length > 0 ? (
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="m-0 mb-3 text-sm font-semibold text-foreground">Daily active users</h2>
          <div className="flex h-16 items-end gap-1">
            {org.dau.map((point) => (
              <div
                key={point.date}
                className="min-w-0 flex-1 rounded-sm bg-primary/70"
                style={{ height: `${Math.max(8, (point.users / maxDau) * 100)}%` }}
                title={`${point.date}: ${point.users}`}
              />
            ))}
          </div>
        </section>
      ) : null}

      {org ? (
        <section className="grid gap-3 md:grid-cols-2">
          <MixCard
            key={`features-${days}`}
            title="Features"
            hint="Page views and actions. Click a row to see who."
            empty="No page views or actions in this range."
            usersById={usersById}
            users={data?.users ?? []}
            items={org.features.map((item) => ({
              key: item.feature,
              label: featureLabel(item.feature),
              value: item.events
            }))}
            valueForUser={(user, key) =>
              user.features.find((item) => item.feature === key)?.events ?? 0
            }
          />
          <MixCard
            key={`actions-${days}`}
            title="Actions"
            hint="How many times each action ran. Click a row to see who."
            empty="No explicit actions in this range."
            usersById={usersById}
            users={data?.users ?? []}
            items={org.actions.map((item) => ({
              key: item.action,
              label: item.action.replaceAll('_', ' '),
              value: item.count
            }))}
            valueForUser={(user, key) =>
              user.actions.find((item) => item.action === key)?.count ?? 0
            }
          />
        </section>
      ) : null}

      {!isLoading && !error && (data?.users.length ?? 0) === 0 ? (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No usage has been recorded in this range.
        </p>
      ) : null}

      {(data?.users.length ?? 0) > 0 ? (
        <section className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[48rem] border-collapse text-left text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">User</th>
                <th className="px-3 py-2 font-medium">Last seen</th>
                <th className="px-3 py-2 font-medium">Time spent</th>
                <th className="px-3 py-2 font-medium">Sessions</th>
                <th className="px-3 py-2 font-medium">Chats</th>
                <th className="px-3 py-2 font-medium">Turns</th>
                <th className="px-3 py-2 font-medium">Top features</th>
              </tr>
            </thead>
            <tbody>
              {data?.users.map((user) => (
                <tr key={user.user_id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium text-foreground">
                    {displayName(user.user_id, usersById)}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {user.last_seen_at ? new Date(user.last_seen_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2">{formatTimeSpent(user.time_spent_seconds)}</td>
                  <td className="px-3 py-2">{user.session_count}</td>
                  <td className="px-3 py-2">{user.conversations_started}</td>
                  <td className="px-3 py-2">
                    {user.turns}
                    {user.avg_turns > 0 ? (
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({formatAvgTurns(user.avg_turns)} avg)
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {user.features.length === 0
                      ? '—'
                      : user.features
                          .slice(0, 3)
                          .map((item) => featureLabel(item.feature))
                          .join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}
    </div>
  )
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="m-0 text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 mb-0 text-2xl font-semibold tracking-tight text-foreground">{value}</p>
      {hint ? <p className="mt-1 mb-0 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

function MixCard({
  title,
  hint,
  empty,
  items,
  users,
  usersById,
  valueForUser
}: {
  title: string
  hint?: string
  empty: string
  items: Array<{ key: string; label: string; value: number }>
  users: UsageUserSummary[]
  usersById: Map<string, { name?: string; username?: string }>
  valueForUser: (user: UsageUserSummary, key: string) => number
}) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const selected = items.find((item) => item.key === selectedKey) ?? null
  const detailUsers = useMemo(
    () => (selected ? usersWithKeyedCount(users, (user) => valueForUser(user, selected.key)) : []),
    [selected, users, valueForUser]
  )

  const max = Math.max(1, ...items.map((item) => item.value))
  const detailMax = Math.max(1, ...detailUsers.map((row) => row.value))

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="m-0 text-sm font-semibold text-foreground">
            {selected ? selected.label : title}
          </h2>
          <p className="mt-1 mb-0 text-xs text-muted-foreground">
            {selected ? `${detailUsers.length} users` : hint}
          </p>
        </div>
        {selected ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setSelectedKey(null)}>
            Back
          </Button>
        ) : null}
      </div>
      {items.length === 0 ? <p className="m-0 text-sm text-muted-foreground">{empty}</p> : null}
      {selected ? (
        detailUsers.length === 0 ? (
          <p className="m-0 text-sm text-muted-foreground">No users recorded for this row.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {detailUsers.map((row) => (
              <li
                key={row.userId}
                className="grid grid-cols-[8rem_1fr_auto] items-center gap-2 text-xs"
              >
                <span className="truncate text-muted-foreground">
                  {displayName(row.userId, usersById)}
                </span>
                <span className="h-2 overflow-hidden rounded-full bg-muted">
                  <span
                    className="block h-full rounded-full bg-primary/70"
                    style={{ width: `${Math.max(6, (row.value / detailMax) * 100)}%` }}
                  />
                </span>
                <span className="tabular-nums text-foreground">{row.value}</span>
              </li>
            ))}
          </ul>
        )
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {items.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                className={cn(
                  'grid w-full grid-cols-[8rem_1fr_auto] items-center gap-2 rounded-sm text-left text-xs hover:bg-muted/50'
                )}
                onClick={() => setSelectedKey(item.key)}
              >
                <span className="truncate text-muted-foreground">{item.label}</span>
                <span className="h-2 overflow-hidden rounded-full bg-muted">
                  <span
                    className="block h-full rounded-full bg-primary/70"
                    style={{ width: `${Math.max(6, (item.value / max) * 100)}%` }}
                  />
                </span>
                <span className="tabular-nums text-foreground">{item.value}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
